import React from 'react';
import classes from './CDCPipeline.module.scss';
import { Stack } from 'new/elements/Stack';
import { SetupBox } from 'new/elements/flows/SetupBox';
import {
  CONNECTORS_RESOURCE_TYPES,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { ResourceCard } from 'js/components/pipelines/cards/ResourceCard';
import { Tooltip } from '@mui/material';

interface CDCPipelineProps {
  onClickResource?: (resource: string) => void;
  onOpenDetailClicked?: (data: any, meta: any) => void;
  source?: any;
  nexset?: any;
  destination?: any;
}

const selectActions = ['activate', 'pause', 'edit', 'activateFlow', 'pauseFlow'];

const AwaitArrow = () => (
  <Tooltip title="Paused">
    <svg viewBox="0 0 800 800">
      <g
        strokeWidth="15"
        stroke="hsl(0, 0%, 60%)"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="29.5 0"
        transform="rotate(315, 400, 400)"
      >
        <line x1="140.5" y1="140.5" x2="659.5" y2="659.5" markerEnd="url(#SvgjsMarker3394)"></line>
      </g>
      <defs>
        <marker
          markerWidth="8"
          markerHeight="8"
          refX="4"
          refY="4"
          viewBox="0 0 8 8"
          orient="auto"
          id="SvgjsMarker3394"
        >
          <polyline
            points="0,4 4,2 0,0"
            fill="none"
            strokeWidth="1.3333333333333333"
            stroke="hsl(0, 0%, 60%)"
            strokeLinecap="round"
            transform="matrix(1,0,0,1,1.3333333333333333,2)"
            strokeLinejoin="round"
          ></polyline>
        </marker>
      </defs>
    </svg>
  </Tooltip>
);
const ActiveArrow = () => (
  <Tooltip title="Active">
    <svg viewBox="0 0 800 800">
      <g
        className={classes.activePath}
        strokeWidth="15"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="29.5 39"
        transform="rotate(315, 400, 400)"
      >
        <line x1="140.5" y1="140.5" x2="659.5" y2="659.5" markerEnd="url(#SvgjsMarker3382)"></line>
      </g>
      <defs>
        <marker
          markerWidth="8"
          markerHeight="8"
          refX="4"
          refY="4"
          viewBox="0 0 8 8"
          orient="auto"
          id="SvgjsMarker3382"
        >
          <polyline
            points="0,4 4,2 0,0"
            fill="none"
            strokeWidth="1.3333333333333333"
            stroke="hsl(205, 69%, 50%)"
            strokeLinecap="round"
            transform="matrix(1,0,0,1,1.3333333333333333,2)"
            strokeLinejoin="round"
          ></polyline>
        </marker>
      </defs>
    </svg>
  </Tooltip>
);

const Arrow = ({ status }: { status: 'await' | 'active' }) => {
  if (status === 'await') return <AwaitArrow />;
  return <ActiveArrow />;
};

export const CDCPipeline = (props: CDCPipelineProps) => {
  const { source, nexset, destination, onOpenDetailClicked } = props;
  const onClickSetup = (resourceType) => () => {
    if (resourceType === RESOURCE_TYPE_SINK && !nexset?.id) {
      return;
    }
    if (props.onClickResource) {
      props.onClickResource(resourceType);
    }
  };

  const firstArrowStatus = () => {
    if (!source || source?.status !== 'ACTIVE' || !nexset || nexset.status !== 'ACTIVE')
      return 'await';
    return 'active';
  };

  const secondArrowStatus = () => {
    if (!nexset || nexset?.status !== 'ACTIVE' || !destination || destination.status !== 'ACTIVE')
      return 'await';
    return 'active';
  };

  return (
    <Stack
      width="100%"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      className={classes.pipeline}
    >
      <ResourceCard
        onOpenDetailClicked={onOpenDetailClicked}
        onClick={onClickSetup(RESOURCE_TYPE_SOURCE)}
        selectActions={selectActions}
        resourceType={RESOURCE_TYPE_SOURCE}
        data={source}
        status={!source ? 'emphasis' : 'normal'}
      />
      <Arrow status={firstArrowStatus()} />
      <ResourceCard
        onOpenDetailClicked={onOpenDetailClicked}
        onClick={onClickSetup(RESOURCE_TYPE_DATA_SET)}
        resourceType={RESOURCE_TYPE_DATA_SET}
        selectActions={selectActions}
        data={nexset}
        showTransform={false}
        showSend
        status="normal"
      />
      <Arrow status={secondArrowStatus()} />
      <ResourceCard
        onOpenDetailClicked={onOpenDetailClicked}
        onClick={onClickSetup(RESOURCE_TYPE_SINK)}
        selectActions={selectActions}
        resourceType={RESOURCE_TYPE_SINK}
        nexset={nexset}
        data={destination}
        status={source?.id && nexset?.id && !destination ? 'emphasis' : 'normal'}
      />
    </Stack>
  );
};
